import { Component } from '@theme/component';
import { ReelPlayback, onScrollFrame, prefersReducedMotion, scrollProgress } from '@theme/ola-reel';

/**
 * Ola · Estreno de marca.
 *
 * Mide el avance del scroll dentro de la sección alta (`track`) y lo deja en la propiedad CSS
 * `--p` (de 0 a 1): el CSS escala el reel, mueve el nombre y muestra la frase y el botón. Con
 * `--p` arriba de 0.7 marca `data-revealed`, para que el botón se pueda tocar.
 * Con movimiento reducido no hay animación: `--p` queda en 1 (el valor del CSS).
 *
 * El reel se reproduce sin sonido solo mientras se ve; los botones de sonido y de pausa los
 * maneja `ReelPlayback`.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - Sección alta con el escenario fijo adentro.
 * @property {HTMLButtonElement} [soundButton]
 * @property {HTMLButtonElement} [pauseButton]
 *
 * @extends {Component<Refs>}
 */
export class OlaEstreno extends Component {
  requiredRefs = ['track'];

  /** Avance desde el que la frase y el botón quedan visibles. */
  static REVEAL_AT = 0.7;

  /** @type {AbortController | undefined} */
  #controller;
  /** @type {ReelPlayback | undefined} */
  #playback;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
  }

  /** Pausa o reproduce el reel. */
  togglePlay() {
    this.#playback?.togglePlay();
  }

  /** Activa o quita el sonido del reel. */
  toggleSound() {
    this.#playback?.toggleSound();
  }

  #setup() {
    this.#teardown();
    this.#controller = new AbortController();

    const video = this.querySelector('video');
    if (video) {
      this.#playback = new ReelPlayback(video, { pause: this.refs.pauseButton, sound: this.refs.soundButton });
    }

    if (prefersReducedMotion()) {
      this.removeAttribute('data-scroll');
      this.style.removeProperty('--p');
      return;
    }

    this.setAttribute('data-scroll', '');
    onScrollFrame(() => this.#update(), this.#controller.signal);
  }

  #teardown() {
    this.#controller?.abort();
    this.#playback?.destroy();
    this.#playback = undefined;
  }

  #update() {
    const progress = scrollProgress(this.refs.track, 'pin');
    this.style.setProperty('--p', progress.toFixed(4));
    this.toggleAttribute('data-revealed', progress >= OlaEstreno.REVEAL_AT);
  }
}

if (!customElements.get('ola-estreno')) {
  customElements.define('ola-estreno', OlaEstreno);
}
